'use client';

import { useEffect } from 'react';

const navigationKeys = new Set([
	' ',
	'ArrowDown',
	'ArrowLeft',
	'ArrowRight',
	'ArrowUp',
	'End',
	'Enter',
	'Escape',
	'Home',
	'PageDown',
	'PageUp',
	'Tab',
]);

/**
 * Keeps focus presentation aligned with the user's current input modality.
 *
 * Browsers and overlay libraries can mark programmatically restored focus as
 * `:focus-visible`, even when a menu was opened or dismissed with a pointer.
 * This marker lets the foundation styles suppress only that incidental ring
 * while preserving strong focus feedback for keyboard navigation.
 */
function FocusModality() {
	useEffect(() => {
		const root = document.documentElement;
		const setPointerModality = () => {
			root.dataset.focusModality = 'pointer';
		};
		const setKeyboardModality = (event: KeyboardEvent) => {
			if (event.altKey || event.ctrlKey || event.metaKey || !navigationKeys.has(event.key)) return;
			root.dataset.focusModality = 'keyboard';
		};

		if (!root.dataset.focusModality) root.dataset.focusModality = 'pointer';
		window.addEventListener('keydown', setKeyboardModality, true);
		window.addEventListener('pointerdown', setPointerModality, true);

		return () => {
			window.removeEventListener('keydown', setKeyboardModality, true);
			window.removeEventListener('pointerdown', setPointerModality, true);
			delete root.dataset.focusModality;
		};
	}, []);

	return null;
}

export { FocusModality };
